import BASEUtils from '../../../@BASE/utils/BASEUtils';
import axios from 'axios';
import jwtDecode from 'jwt-decode';
import jwtServiceConfig from './jwtServiceConfig';

/* eslint-disable camelcase */

const decode = (t) => JSON.parse(atob(t.split('.')[1]));
const hms = (exp) =>
  exp ? new Date(exp * 1000).toISOString().slice(11, 19) : '<none>';

class JwtService extends BASEUtils.EventEmitter {
  init() {
    this.setInterceptors();
    this.handleAuthentication();
  }

  setInterceptors = () => {
    axios.interceptors.response.use(
      (response) => {
        // ⬇️ New: pick up rotated token from header
        const hdr = response?.headers;
        const rotated = hdr && (hdr['x-access-token'] || hdr['X-Access-Token']);
        if (rotated) {
          const before = localStorage.getItem('jwt_access_token');
          const bExp = before ? decode(before).exp : null;
          const a = decode(rotated);
          const delta = bExp ? a.exp - bExp : null;

          console.log(
            `[AUTH rotate] sid=${a.sid}  expires_at (UTC): ${hms(bExp)} → ${hms(
              a.exp
            )}` + (delta !== null ? `  Δ=${delta}s` : '')
          );

          this.setSession(rotated); // updates localStorage + axios default header
        }
        return response;
      },
      (err) => {
        return new Promise((resolve, reject) => {
          if (
            err.response.status === 401 &&
            err.config &&
            !err.config.__isRetryRequest
          ) {
            // if you ever get an unauthorized response, logout the user
            this.emit('onAutoLogout', 'Invalid access_token');
            this.setSession(null);
          }
          throw err;
        });
      }
    );
  };

  handleAuthentication = () => {
    const access_token = this.getAccessToken();

    if (!access_token) {
      this.emit('onNoAccessToken');

      return;
    }

    if (this.isAuthTokenValid(access_token)) {
      this.setSession(access_token);
      this.emit('onAutoLogin', true);
    } else {
      this.emit('onAutoLogout', 'access_token expired');
      this.setSession(null);
    }
  };

  createUser = (data) => {
    return new Promise((resolve, reject) => {
      axios.post(jwtServiceConfig.signUp, data).then((response) => {
        if (response.data.user) {
          this.setSession(response.data.access_token);
          resolve(response.data.user);
          this.emit('onLogin', response.data.user);
        } else {
          reject(response.data.error);
        }
      });
    });
  };

  signInWithUsernameAndPassword = (username, password) => {
    return new Promise((resolve, reject) => {
      axios
        .post(jwtServiceConfig.signIn, {
          username: username,
          password: password
        })
        .catch((error) => {
          if (error.response && error.response.status === 404) {
            reject([
              {
                type: 'identifier',
                message: 'Check your email or username.'
              }
            ]);
          } else if (error.response && error.response.status === 401) {
            reject([
              {
                type: 'password',
                message: 'Check your password.'
              }
            ]);
          } else {
            reject([
              {
                type: 'server',
                message: 'Login failed. Please try again.'
              }
            ]);
          }
        })
        .then((response) => {
          if (response && response.data) {
            this.setSession(response.data.access_token);
            resolve(response.data);
            this.emit('onLogin', response.data);
          } else {
            reject([
              {
                type: 'server',
                message: 'Login failed. Please try again.'
              }
            ]);
          }
        });
    });
  };

  signInWithEmailAndPassword = (email, password) => {
    return new Promise((resolve, reject) => {
      axios
        .post(jwtServiceConfig.signIn, {
          email: email,
          password: password
        })
        .catch((error) => {
          if (error.response.status === 404) {
            reject([
              {
                type: 'identifier',
                message: 'Check your email or username.'
              }
            ]);
          } else if (error.response.status === 401) {
            reject([
              {
                type: 'password',
                message: 'Check your password.'
              }
            ]);
          }
        })
        .then((response) => {
          if (response.data) {
            this.setSession(response.data.access_token);

            resolve(response.data);
            this.emit('onLogin', response.data);
          } else {
            reject(response.data.error);
          }
        });
    });
  };

  signInWithToken = () => {
    return new Promise((resolve, reject) => {
      const token = this.getAccessToken();

      axios
        .get(jwtServiceConfig.accessToken, {
          data: {
            access_token: this.getAccessToken()
          },
          headers: {
            Authorization: `Bearer ${token}`
          }
        })
        .then((response) => {
          if (response.data) {
            this.setSession(response.data.access_token);
            resolve(response.data);
          } else {
            this.logout();
            reject(new Error('Failed to login with token.'));
          }
        })
        .catch((error) => {
          this.logout();
          reject(new Error('Failed to login with token.'));
        });
    });
  };

  updateUserData = (user) => {
    return axios.post(jwtServiceConfig.updateUser, {
      user
    });
  };

  setSession = (access_token) => {
    if (access_token) {
      localStorage.setItem('jwt_access_token', access_token);
      axios.defaults.headers.common.Authorization = `Bearer ${access_token}`;
    } else {
      localStorage.removeItem('jwt_access_token');
      delete axios.defaults.headers.common.Authorization;
    }
  };

  logout = () => {
    this.emit('onLogout', 'Logged out');
    this.setSession(null);
  };

  isAuthTokenValid = (access_token) => {
    if (!access_token) {
      return false;
    }
    const decoded = jwtDecode(access_token);
    const currentTime = Date.now() / 1000;
    if (decoded.exp < currentTime) {
      console.warn('access token expired');
      return false;
    }

    return true;
  };

  getAccessToken = () => {
    return window.localStorage.getItem('jwt_access_token');
  };

  requestNewPassword = (identifier, env) => {
    const payload = identifier.includes('@')
      ? { email: identifier, env }
      : { username: identifier, env };

    return new Promise((resolve, reject) => {
      axios
        .post(jwtServiceConfig.requestNewPassword, payload)
        .then((response) => {
          if (response.data) {
            resolve(response.data);
            this.emit('onNewPasswordRequest', response.data);
          } else {
            reject(response.data.error);
          }
        })
        .catch((error) => {
          if (error.response.status === 404) {
            reject([
              {
                type: 'identifier',
                message: 'Check your email address or username'
              }
            ]);
          } else if (error.response.status === 403) {
            reject([
              {
                type: 'identifier',
                message: error.response.data.message
              }
            ]);
          }
        });
    });
  };

  resetPassword = (email, reset_token, password) => {
    return new Promise((resolve, reject) => {
      axios
        .post(jwtServiceConfig.resetPassword, {
          email: email,
          reset_token: reset_token,
          password: password
        })
        .catch((error) => {
          if (error.response.status === 404) {
            reject([
              {
                type: 'email',
                message: 'Check your email address'
              }
            ]);
          } else if (error.response.status === 401) {
            reject([
              {
                type: 'reset_token',
                message: 'Check your reset token'
              }
            ]);
          } else if (error.response.status === 500) {
            reject([
              {
                type: 'server',
                message: 'Something went wrong. Please try again later.'
              }
            ]);
          }
        })
        .then((response) => {
          if (response.data) {
            resolve(response.data);
            this.emit('onResetPassword', response.data);
          } else {
            reject(response.data.error);
          }
        });
    });
  };

  terminateSession = () => {
    const token = this.getAccessToken();

    if (token) {
      axios.post(
        jwtServiceConfig.logout,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
    }
  };
}

const instance = new JwtService();

export default instance;
